import { useEffect, useState, type FormEvent } from 'react';
import { api, type AdminUser, type TenantSummary } from '../api';
import { useAuth } from '../auth';
import { Banner, Card, SelectField, TextField, relativeTime } from '../components/ui';

export function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'super_admin' | 'tenant_admin'>('tenant_admin');
  const [tenantIds, setTenantIds] = useState<string[]>([]);

  const load = async () => {
    setUsers(await api.get<AdminUser[]>('/api/users'));
    setTenants(await api.get<TenantSummary[]>('/api/tenants'));
  };
  useEffect(() => { void load().catch(() => setUsers([])); }, []);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post('/api/users', { email, password, role, tenantIds });
      setEmail(''); setPassword(''); setTenantIds([]);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create user');
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id: string, body: unknown) => {
    setError('');
    try {
      await api.patch(`/api/users/${id}`, body);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  };

  return (
    <>
      <h1 className="page-title">Admin users</h1>
      <p className="page-sub" style={{ marginBottom: 20 }}>
        Super admins manage every tenant. Tenant admins only see the tenants assigned to them.
      </p>

      {error && <Banner kind="error">{error}</Banner>}

      <Card title="Create user">
        <form onSubmit={create}>
          <div className="grid two">
            <TextField label="Email" value={email} onChange={setEmail} type="email" />
            <TextField
              label="Initial password" value={password} onChange={setPassword} type="password"
              hint="At least 10 characters. The user can change it after signing in."
            />
            <SelectField
              label="Role" value={role} onChange={(v) => setRole(v as typeof role)}
              options={[
                { value: 'tenant_admin', label: 'Tenant admin' },
                { value: 'super_admin', label: 'Super admin' },
              ]}
            />
          </div>
          {role === 'tenant_admin' && (
            <div style={{ marginBottom: 12 }}>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--text-dim)', marginBottom: 6 }}>
                Tenants this user may manage
              </span>
              <div className="row">
                {tenants.map((t) => (
                  <label className="check" key={t.id} style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox" checked={tenantIds.includes(t.id)}
                      onChange={(e) => setTenantIds((ids) =>
                        e.target.checked ? [...ids, t.id] : ids.filter((i) => i !== t.id))}
                    />
                    <span>{t.name}</span>
                  </label>
                ))}
                {tenants.length === 0 && <span className="faint small">No tenants exist yet.</span>}
              </div>
            </div>
          )}
          <button className="primary" type="submit" disabled={busy || !email || password.length < 10}>
            {busy ? 'Creating…' : 'Create user'}
          </button>
        </form>
      </Card>

      <Card title="Existing users">
        {users === null ? <p className="faint">Loading…</p> : (
          <table>
            <thead>
              <tr><th>Email</th><th>Role</th><th>Tenants</th><th>Created</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}{u.id === me?.id && <span className="faint small"> (you)</span>}</td>
                  <td><span className="badge proto">{u.role === 'super_admin' ? 'super admin' : 'tenant admin'}</span></td>
                  <td className="dim small">
                    {u.role === 'super_admin' ? 'all' :
                      u.tenantIds.map((id) => tenants.find((t) => t.id === id)?.name ?? id).join(', ') || '—'}
                  </td>
                  <td className="dim nowrap">{relativeTime(u.createdAt)}</td>
                  <td>{u.disabled ? <span className="badge error">disabled</span> : <span className="badge success">active</span>}</td>
                  <td className="nowrap">
                    {u.id !== me?.id && (
                      <>
                        <button className="ghost tiny" onClick={() => patch(u.id, { disabled: !u.disabled })}>
                          {u.disabled ? 'Enable' : 'Disable'}
                        </button>
                        <button
                          className="ghost tiny"
                          onClick={() => {
                            const p = prompt(`New password for ${u.email} (at least 10 characters)`);
                            if (p) void patch(u.id, { password: p });
                          }}
                        >Reset password</button>
                        <button
                          className="ghost tiny danger"
                          onClick={async () => {
                            if (!confirm(`Delete ${u.email}?`)) return;
                            try { await api.del(`/api/users/${u.id}`); await load(); }
                            catch (e) { setError(e instanceof Error ? e.message : 'Delete failed'); }
                          }}
                        >Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
